import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Tabs from "@/components/controls/Tabs";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import { List, Preferences } from "@dreamhub/designsystem/icons";
import { ContractCarryOverOptions } from "@/pages/contracts/hooks";
import { DEFAULT_CARRY_OVER } from "./types";

type CarryOverTab = "allContractData" | "customize";

interface CarryOverSectionProps {
  options: ContractCarryOverOptions;
  onOptionsChange: (options: ContractCarryOverOptions) => void;
}

// Figma exports the tab icons at 24px; the tabs render at 18px, so fix the
// box and let the glyph fill it (same fix as DreamerModeTabs).
const TabIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex size-[18px] shrink-0 items-center justify-center [&>svg]:size-full">
    {children}
  </span>
);

const CARRY_OVER_KEYS = [
  "retentionWorkflowsData",
  "filesAndAttachments",
  "onboardingStatusAndWorkflows",
  "peopleInContract",
  "contractOwners"
] as const satisfies ReadonlyArray<keyof ContractCarryOverOptions>;

const CarryOverSection: React.FC<CarryOverSectionProps> = ({
  options,
  onOptionsChange
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<CarryOverTab>("allContractData");

  const handleTabChange = (tab: CarryOverTab) => {
    setActiveTab(tab);
    if (tab === "allContractData") {
      onOptionsChange(DEFAULT_CARRY_OVER);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      data-test="duplicate-carry-over-section"
    >
      <Text variant="smSubtitle">
        {t("contracts.duplicate.carryOver.title")}
      </Text>
      <div className="flex flex-col gap-3 bg-surface-elevation1 rounded-xl border border-border-border1 p-4">
        <div className="w-fit">
          <Tabs
            options={{
              allContractData: (
                <span className="flex items-center gap-1">
                  <TabIcon>
                    <List />
                  </TabIcon>
                  {t("contracts.duplicate.carryOver.allContractData")}
                </span>
              ),
              customize: (
                <span className="flex items-center gap-1">
                  <TabIcon>
                    <Preferences />
                  </TabIcon>
                  {t("contracts.duplicate.carryOver.customize")}
                </span>
              )
            }}
            value={activeTab}
            valueSelected={(value) => handleTabChange(value as CarryOverTab)}
            dataTest="duplicate-carry-over-tabs"
          />
        </div>
        {activeTab === "allContractData" ? (
          <Text variant="bodySecondary">
            {t("contracts.duplicate.carryOver.allContractDataDescription")}
          </Text>
        ) : (
          <>
            <Text variant="bodySecondary">
              {t("contracts.duplicate.carryOver.customizeDescription")}
            </Text>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 w-fit">
              {CARRY_OVER_KEYS.map((key) => (
                <SingleCheckbox
                  key={key}
                  id={`carry-over-${key}`}
                  label={t(`contracts.duplicate.carryOver.options.${key}`)}
                  isChecked={options[key]}
                  onCheckedChange={(checked) =>
                    onOptionsChange({ ...options, [key]: checked })
                  }
                  labelVariant="body"
                  data-test={`carry-over-option-${key}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CarryOverSection;
